import React, { useState, useEffect } from "react";
import { Input } from "./input";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Calendar as CalIcon } from "lucide-react";
import { es } from "date-fns/locale";

// ISO YYYY-MM-DD <-> ES DD/MM/AAAA helpers
const isoToES = (iso) => {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
};

const esToISO = (es) => {
  if (!es) return "";
  const m = String(es).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return "";
  const d = m[1].padStart(2, "0");
  const mo = m[2].padStart(2, "0");
  const y = m[3];
  const dNum = Number(d), moNum = Number(mo);
  if (moNum < 1 || moNum > 12 || dNum < 1 || dNum > 31) return "";
  return `${y}-${mo}-${d}`;
};

const isoToDate = (iso) => {
  if (!iso) return undefined;
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};

const dateToISO = (dt) => {
  if (!dt) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Auto-slash mask while typing
const maskES = (raw) => {
  const digits = String(raw).replace(/\D/g, "").slice(0, 8);
  const parts = [];
  if (digits.length >= 2) parts.push(digits.slice(0, 2)); else if (digits.length > 0) parts.push(digits);
  if (digits.length >= 4) parts.push(digits.slice(2, 4)); else if (digits.length > 2) parts.push(digits.slice(2));
  if (digits.length > 4) parts.push(digits.slice(4, 8));
  return parts.join("/");
};

/**
 * DateInputES: siempre muestra DD/MM/AAAA. Guarda ISO (YYYY-MM-DD).
 * El icono abre un calendario shadcn (react-day-picker) en español.
 */
export const DateInputES = React.forwardRef(function DateInputES(
  { value, onChange, className = "", placeholder = "DD/MM/AAAA", "data-testid": testId, disabled, ...rest },
  ref
) {
  const [text, setText] = useState(() => isoToES(value));
  const [open, setOpen] = useState(false);

  useEffect(() => { setText(isoToES(value)); }, [value]);

  const emit = (iso) => {
    if (!onChange) return;
    onChange({ target: { value: iso } });
  };

  const handleTextChange = (e) => {
    const masked = maskES(e.target.value);
    setText(masked);
    if (masked.length === 10) {
      const iso = esToISO(masked);
      if (iso) emit(iso);
    } else if (masked === "") {
      emit("");
    }
  };

  const handleBlur = () => {
    if (text === "") { emit(""); return; }
    const iso = esToISO(text);
    if (iso) {
      emit(iso);
      setText(isoToES(iso));
    } else {
      setText(isoToES(value));
    }
  };

  const handleCalendarSelect = (dt) => {
    if (!dt) return;
    const iso = dateToISO(dt);
    emit(iso);
    setText(isoToES(iso));
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <Input
        ref={ref}
        type="text"
        inputMode="numeric"
        value={text}
        onChange={handleTextChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        data-testid={testId}
        disabled={disabled}
        className="pr-9"
        {...rest}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            tabIndex={-1}
            aria-label="Abrir calendario"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#026FCB] dark:hover:text-sky-400 disabled:opacity-40 transition-colors"
          >
            <CalIcon size={15} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={isoToDate(value)}
            onSelect={handleCalendarSelect}
            defaultMonth={isoToDate(value)}
            locale={es}
            weekStartsOn={1}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
});

export default DateInputES;
